/**
 * Edit a manual monitor's config, in place on its card.
 *
 * Prefilled from the saved spec the backend returns - host, path, scheme, label, and the
 * *shape* of the key lifecycle (endpoints, header, key_field). A key value is never sent
 * to the browser, so the API-key field starts blank: leaving it blank keeps the existing
 * key/lifecycle, typing a new one replaces it, and "remove key config" turns detailed
 * mode off. Only fields that change are sent, so an accidental blank never wipes config.
 */

import { useEffect, useState } from 'react'

import type { ManualTargetSpec, ManualTargetUpdate } from '../api/types'
import { api } from '../api/client'
import { cn } from '../lib/cn'
import {
  EMPTY_ENDPOINT,
  EndpointFieldset,
  buildEndpoint,
  inputClass,
} from './ui/EndpointFields'
import type { EndpointFields } from './ui/EndpointFields'

interface EditManualFormProps {
  targetId: string
  onSubmit: (body: ManualTargetUpdate) => Promise<unknown>
  onClose: () => void
  isPending: boolean
  error: string | null
}

export function EditManualForm({
  targetId,
  onSubmit,
  onClose,
  isPending,
  error,
}: EditManualFormProps) {
  const [spec, setSpec] = useState<ManualTargetSpec | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [host, setHost] = useState('')
  const [path, setPath] = useState('')
  const [scheme, setScheme] = useState<'http' | 'https'>('https')
  const [label, setLabel] = useState('')

  // Key section: whether it is being changed at all, plus the new values.
  const [showKey, setShowKey] = useState(false)
  const [clearKey, setClearKey] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [keyHeader, setKeyHeader] = useState('')
  const [issue, setIssue] = useState<EndpointFields>({ ...EMPTY_ENDPOINT })
  const [verify, setVerify] = useState<EndpointFields>({ ...EMPTY_ENDPOINT, method: 'GET' })
  const [keyError, setKeyError] = useState<string | null>(null)

  // Load the saved spec once and prefill.
  useEffect(() => {
    let alive = true
    api
      .manualSpec(targetId)
      .then((loaded) => {
        if (!alive) return
        setSpec(loaded)
        setHost(loaded.host)
        setPath(loaded.path ?? '')
        setScheme(loaded.scheme ?? 'https')
        setLabel(loaded.label ?? '')
        const cfg = loaded.api_key_config
        if (cfg) {
          setKeyHeader(cfg.api_key_header ?? '')
          if (cfg.issue_api_key)
            setIssue({
              path: cfg.issue_api_key.path ?? '',
              method: cfg.issue_api_key.method ?? 'POST',
              payload: cfg.issue_api_key.payload
                ? JSON.stringify(cfg.issue_api_key.payload, null, 2)
                : '',
              keyField: cfg.issue_api_key.key_field ?? '',
            })
          if (cfg.verify_api_key)
            setVerify({
              path: cfg.verify_api_key.path ?? '',
              method: cfg.verify_api_key.method ?? 'GET',
              payload: cfg.verify_api_key.payload
                ? JSON.stringify(cfg.verify_api_key.payload, null, 2)
                : '',
              keyField: '',
            })
        }
      })
      .catch((err) => alive && setLoadError((err as Error).message))
    return () => {
      alive = false
    }
  }, [targetId])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setKeyError(null)
    if (!spec) return

    const body: ManualTargetUpdate = {}
    // Only send what actually changed, so an untouched field never overwrites.
    if (host.trim() && host.trim() !== spec.host) body.host = host.trim()
    if ((path.trim() || null) !== (spec.path ?? null)) body.path = path.trim() || null
    if (scheme !== (spec.scheme ?? 'https')) body.scheme = scheme
    if (label.trim() !== (spec.label ?? '')) body.label = label.trim() || undefined

    if (showKey) {
      if (clearKey) {
        body.clear_api_key = true
      } else {
        let issueCfg
        let verifyCfg
        try {
          issueCfg = buildEndpoint(issue, 'issue')
          verifyCfg = buildEndpoint(verify, 'verify')
        } catch (err) {
          setKeyError((err as Error).message)
          return
        }
        if (keyHeader.trim() || issueCfg || verifyCfg) {
          body.api_key_config = {
            ...(keyHeader.trim() ? { api_key_header: keyHeader.trim() } : {}),
            ...(issueCfg ? { issue_api_key: issueCfg } : {}),
            ...(verifyCfg ? { verify_api_key: verifyCfg } : {}),
          }
        }
        if (apiKey.trim()) body.api_key = apiKey.trim()
      }
    }

    await onSubmit(body)
  }

  if (loadError) {
    return (
      <div className="border-b px-4 py-3 text-xs text-critical">
        Could not load this monitor’s config: {loadError}
        <button type="button" onClick={onClose} className="ml-2 underline">
          Close
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-3 border-b bg-surface-sunken/40 px-4 py-3">
      <p className="text-xs font-semibold text-ink-secondary">Edit monitor</p>

      <div className="grid gap-2 sm:grid-cols-[6rem_minmax(0,1fr)_8rem]">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-ink-muted">Scheme</span>
          <select
            value={scheme}
            onChange={(e) => setScheme(e.target.value as 'http' | 'https')}
            className={inputClass}
          >
            <option value="https">https</option>
            <option value="http">http</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-ink-muted">Host</span>
          <input value={host} onChange={(e) => setHost(e.target.value)} className={inputClass} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-ink-muted">Path</span>
          <input value={path} onChange={(e) => setPath(e.target.value)} className={inputClass} />
        </label>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] text-ink-muted">Label</span>
        <input value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />
      </label>

      <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-secondary">
        <input
          type="checkbox"
          checked={showKey}
          onChange={(e) => setShowKey(e.target.checked)}
          className="h-3.5 w-3.5 accent-[var(--color-accent)]"
        />
        Change the API key config
      </label>

      {showKey ? (
        <div className="space-y-3 rounded-md border p-3">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-critical">
            <input
              type="checkbox"
              checked={clearKey}
              onChange={(e) => setClearKey(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Remove key config (turn detailed mode off)
          </label>

          {!clearKey ? (
            <>
              <p className="text-[11px] text-ink-muted">
                Leave the key blank to keep the current one; type a new one to replace it.
                The verify call sends the key in the header below.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-ink-muted">API key</span>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="unchanged"
                    autoComplete="off"
                    className={inputClass}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-ink-muted">Key header</span>
                  <input
                    value={keyHeader}
                    onChange={(e) => setKeyHeader(e.target.value)}
                    placeholder="X-API-Key"
                    className={inputClass}
                  />
                </label>
              </div>
              <EndpointFieldset title="Issue endpoint" fields={issue} onChange={setIssue} showKeyField />
              <EndpointFieldset title="Verify endpoint" fields={verify} onChange={setVerify} />
            </>
          ) : null}

          {keyError ? <p className="text-xs text-critical">{keyError}</p> : null}
        </div>
      ) : null}

      {error ? <p className="text-xs text-critical">{error}</p> : null}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={isPending || !spec}
          className={cn(
            'rounded-md border bg-accent-soft px-3 py-1.5 text-xs font-semibold text-ink',
            'transition-colors hover:brightness-95 disabled:opacity-60',
          )}
        >
          {isPending ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border px-3 py-1.5 text-xs text-ink-secondary hover:bg-surface-sunken"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
